import React, { useState, useCallback } from 'react';
import { useTranslation } from '../../i18n/hooks';
import { Button, Input } from '../../ui';
import AuthService from '../services/AuthService';
import ValidationUtils from '../../../modules/utils/ValidationUtils';
import './ForgotPasswordForm.css';

/**
 * ForgotPasswordForm component - Form for requesting password reset
 * @param {Object} props - Component props
 * @param {Function} props.onSuccess - Success callback
 * @param {Function} props.onBackToLogin - Back to login callback
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ForgotPasswordForm component
 */
const ForgotPasswordForm = function ForgotPasswordForm({
  onSuccess,
  onBackToLogin,
  className = ''
}) {
  const { t, language } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [touched, setTouched] = useState({ email: false });

  // Validation - Uses ValidationUtils from modules/utils (Component 12)
  const emailValidation = ValidationUtils.validateEmail(email, language || 'bg');
  
  const errors = {
    email: touched.email && !email ? t('auth.validation.emailRequired') : 
           touched.email && !emailValidation.isValid ? 
             (emailValidation.errors && emailValidation.errors.length > 0 ? emailValidation.errors[0] : t('auth.validation.emailInvalid')) : null
  };

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
    setTouched(prev => ({ ...prev, email: true }));
    setSubmitError(null);
  }, []);

  const handleSubmit = useCallback(async (ev) => {
    ev.preventDefault();
    console.log('🔍 ForgotPasswordForm: handleSubmit called with email:', email);
    
    setTouched({ email: true });
    setSubmitError(null);
    setSubmitSuccess(false);

    // Validate email before submitting
    if (!email) {
      console.log('❌ ForgotPasswordForm: Email is empty');
      setSubmitError(t('auth.validation.emailRequired'));
      return;
    }
    
    const emailValidationResult = ValidationUtils.validateEmail(email, language || 'bg');
    if (!emailValidationResult.isValid) {
      console.log('❌ ForgotPasswordForm: Email is invalid:', email);
      setSubmitError(emailValidationResult.errors && emailValidationResult.errors.length > 0 
        ? emailValidationResult.errors[0] 
        : t('auth.validation.emailInvalid'));
      return;
    }

    console.log('✅ ForgotPasswordForm: Validation passed, calling API...');
    setIsSubmitting(true);
    try {
      const { data, error } = await AuthService.forgotPassword(email);
      console.log('📡 ForgotPasswordForm: API response:', { data, error });
      
      if (error) {
        setSubmitError(error.message || t('errors.generic'));
        return;
      }
      if (data) {
        setSubmitSuccess(true);
        if (onSuccess) onSuccess(data);
      } else {
        setSubmitError(t('errors.generic'));
      }
    } catch (e) {
      console.log('💥 ForgotPasswordForm: Exception caught:', e);
      setSubmitError(t('errors.network'));
    } finally {
      setIsSubmitting(false);
    }
  }, [email, onSuccess, t]);

  const handleBackToLogin = useCallback(() => {
    if (onBackToLogin) {
      onBackToLogin();
    }
  }, [onBackToLogin]);

  return (
    <div className={`arbiwin-form arbiwin-form--forgot-password ${className}`}>
      <div className="arbiwin-form__header">
        <h2 className="arbiwin-form__title">
          {t('auth.forgotPassword.title')}
        </h2>
        <p className="arbiwin-form__subtitle">
          {t('auth.forgotPassword.subtitle')}
        </p>
      </div>

      {submitSuccess ? (
        <div className="arbiwin-form__group">
          <div className="arbiwin-form__success" style={{
            background: '#d4edda',
            border: '1px solid #c3e6cb',
            color: '#155724',
            padding: '12px 16px',
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>✅</span>
              <strong>{t('auth.forgotPassword.success')}</strong>
            </div>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
              {t('auth.forgotPassword.successMessage')}
            </p>
            <button 
              onClick={handleBackToLogin}
              style={{
                display: 'inline-block',
                background: '#28a745',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.target.style.background = '#218838'}
              onMouseOut={(e) => e.target.style.background = '#28a745'}
            >
              {t('auth.forgotPassword.backToLogin')}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="arbiwin-form__form">
          {submitError && (
            <div className="arbiwin-form__error" style={{
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              color: '#721c24',
              padding: '12px 16px',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              {submitError}
            </div>
          )}

          <div className="arbiwin-form__group">
            <label htmlFor="email" className="arbiwin-form__label">
              {t('auth.fields.email')}
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder={t('auth.placeholders.email')}
              error={errors.email}
              disabled={isSubmitting}
              className="arbiwin-form__input"
              required
            />
            {errors.email && (
              <span className="arbiwin-form__field-error">
                {errors.email}
              </span>
            )}
          </div>

          <div className="arbiwin-form__footer">
            <Button
              type="submit"
              variant="primary"
              size="large"
              disabled={isSubmitting}
              className="arbiwin-form__submit-btn"
              fullWidth
              onClick={() => console.log('🔘 ForgotPasswordForm: Button clicked!')}
            >
              {isSubmitting ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.sendResetLink')}
            </Button>

            <div className="arbiwin-form__links">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="arbiwin-form__link"
                disabled={isSubmitting}
              >
                {t('auth.forgotPassword.backToLogin')}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default ForgotPasswordForm;
